import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik, FormikErrors } from 'formik';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  CheckBox,
  DatePickerCustom,
  Input,
  RadioButtonGroup,
  Select,
  TopProgress,
  Typography,
} from '../../../../components';
import { openstackPlanService } from '../../../../api';
import { useUi } from '../../../../context/ui.context';
import type { DateValue } from '@internationalized/date';
import {
  dateValueToIsoString,
  parseApiDateToDateValue,
} from '../../../../components/date-picker/utils';
import {
  PageWrapper,
  HeaderRow,
  SectionCard,
  SectionHeading,
  SectionDescription,
  FieldsRow,
  FieldCol,
  DynamicRow,
  RowActions,
  FooterActions,
  AddButtonSlot,
} from './styles';

type RateType = 'flat' | 'tiered';

type PricingRuleAttributeChoice = {
  display?: string;
  sub?: string;
};

type PricingRuleAttribute = {
  type: string;
  name: string;
  value_size?: string;
  choices?: string[];
  choices_dict?: Record<string, PricingRuleAttributeChoice>;
};

type PricingRuleMetric = {
  name: string;
  help_text?: string;
  reaggregation?: string;
  billing_based_on_existence?: boolean;
};

type PricingRuleResource = {
  name: string;
  internal_name: string;
  type: string;
  id: number;
  metric_display: boolean;
  metrics?: PricingRuleMetric[];
  attribute_display: boolean;
  attributes: PricingRuleAttribute[];
};

type PricingRuleCreateOptions = {
  resources: PricingRuleResource[];
  attribute_units: Record<string, string>;
  time_units: Record<string, string>;
  number_operators: Record<string, string>;
  string_operators: Record<string, string>;
  plans?: any[];
  currencies?: any[];
};

type PriceTier = {
  f: string;
  p: string;
  t?: string;
};

type ConditionForm = {
  attribute: string;
  operator: string;
  value: string;
  attribute_unit: string | null;
};

type ModifierForm = {
  name: string;
  attribute: string;
  operator: string;
  value: string;
  attribute_unit: string | null;
  price: string;
  price_is_percent: boolean;
  time_unit: string | null;
};

type RuleFormValues = {
  display_name: string;
  resource: number | '';
  pricing: {
    attribute: string;
    attribute_unit: string | null;
    time_unit: string | null;
    prices: PriceTier[];
  };
  start_dt: DateValue | null;
  end_dt: DateValue | null;
  calculate_metrics_pricing_per_time_unit: boolean;
  free_resources_for_related_resource: string;
  conditions: ConditionForm[];
  modifiers: ModifierForm[];
};

const LIST_OPERATORS = ['in', 'ni'] as const;
const UNIT_SIZE_CODES = ['k', 'm', 'g', 't', 'b', 'p', 'e'];

const createEmptyPriceTier = (): PriceTier => ({
  f: '0',
  p: '',
  t: '∞',
});

const createEmptyCondition = (): ConditionForm => ({
  attribute: '',
  operator: '',
  value: '',
  attribute_unit: null,
});

const createEmptyModifier = (): ModifierForm => ({
  name: '',
  attribute: '',
  operator: '',
  value: '',
  attribute_unit: null,
  price: '',
  price_is_percent: false,
  time_unit: null,
});

const defaultFormValues: RuleFormValues = {
  display_name: '',
  resource: '',
  pricing: {
    attribute: '',
    attribute_unit: null,
    time_unit: null,
    prices: [createEmptyPriceTier()],
  },
  start_dt: null,
  end_dt: null,
  calculate_metrics_pricing_per_time_unit: false,
  free_resources_for_related_resource: '',
  conditions: [],
  modifiers: [],
};

const isListOperator = (operator: string) =>
  LIST_OPERATORS.includes(operator as (typeof LIST_OPERATORS)[number]);

const normalizeOperatorMap = (source: any): Record<string, string> => {
  if (!source) {
    return {};
  }
  if (Array.isArray(source)) {
    return source.reduce((acc: Record<string, string>, item: any) => {
      const key = String(item);
      acc[key] = key;
      return acc;
    }, {});
  }
  return source;
};

const normalizeOptions = (raw: any): PricingRuleCreateOptions => ({
  resources: Array.isArray(raw?.resources) ? raw.resources : [],
  attribute_units: raw?.attribute_units ?? {},
  time_units: raw?.time_units ?? {},
  number_operators: normalizeOperatorMap(raw?.number_operators),
  string_operators: normalizeOperatorMap(raw?.string_operators),
  plans: raw?.plans ?? [],
  currencies: raw?.currencies ?? [],
});

const mapRuleFromApi = (rule: any): RuleFormValues => {
  const apiPrices = Array.isArray(rule?.pricing?.prices) ? rule.pricing.prices : [];
  const prices = apiPrices.length
    ? apiPrices.map((price: any, index: number) => ({
        f:
          price?.f !== undefined && price?.f !== null
            ? String(price.f)
            : index === 0
            ? '0'
            : '',
        p:
          price?.p !== undefined && price?.p !== null
            ? String(price.p)
            : '',
        t:
          price?.t !== undefined && price?.t !== null
            ? String(price.t)
            : '∞',
      }))
    : [createEmptyPriceTier()];

  return {
    display_name: rule?.display_name ?? '',
    resource:
      rule?.resource !== undefined && rule?.resource !== null
        ? Number(rule.resource)
        : '',
    pricing: {
      attribute: rule?.pricing?.attribute ?? '',
      attribute_unit:
        rule?.pricing?.attribute_unit !== undefined
          ? rule.pricing.attribute_unit
          : null,
      time_unit:
        rule?.pricing?.time_unit !== undefined
          ? rule.pricing.time_unit
          : null,
      prices,
    },
    start_dt: parseApiDateToDateValue(rule?.start_dt),
    end_dt: parseApiDateToDateValue(rule?.end_dt),
    calculate_metrics_pricing_per_time_unit: Boolean(
      rule?.calculate_metrics_pricing_per_time_unit,
    ),
    free_resources_for_related_resource:
      rule?.free_resources_for_related_resource !== undefined &&
      rule?.free_resources_for_related_resource !== null
        ? String(rule.free_resources_for_related_resource)
        : '',
    conditions: Array.isArray(rule?.conditions)
      ? rule.conditions.map((condition: any) => ({
          attribute: condition?.attribute ?? '',
          operator: condition?.operator ?? '',
          value:
            condition?.value !== undefined && condition?.value !== null
              ? String(condition.value)
              : '',
          attribute_unit:
            condition?.attribute_unit !== undefined
              ? condition.attribute_unit
              : null,
        }))
      : [],
    modifiers: Array.isArray(rule?.modifiers)
      ? rule.modifiers.map((modifier: any) => ({
          name: modifier?.name ?? '',
          attribute: modifier?.attribute ?? '',
          operator: modifier?.operator ?? '',
          value:
            modifier?.value !== undefined && modifier?.value !== null
              ? String(modifier.value)
              : '',
          attribute_unit:
            modifier?.attribute_unit !== undefined
              ? modifier.attribute_unit
              : null,
          price:
            modifier?.price !== undefined && modifier?.price !== null
              ? String(modifier.price)
              : '',
          price_is_percent: Boolean(modifier?.price_is_percent),
          time_unit:
            modifier?.price_is_percent
              ? null
              : modifier?.time_unit ?? null,
        }))
      : [],
  };
};

const recalcTierBounds = (prices: PriceTier[]): PriceTier[] => {
  if (!prices.length) {
    return [createEmptyPriceTier()];
  }

  return prices.map((tier, index) => {
    if (tier.f === '' || tier.f === null || tier.f === undefined) {
      return { ...tier, t: '∞' };
    }
    const currentFrom = Number(tier.f);
    if (Number.isNaN(currentFrom)) {
      return { ...tier, t: '∞' };
    }
    let nextLimit: number | null = null;
    prices.forEach((other, otherIndex) => {
      if (otherIndex === index) {
        return;
      }
      const otherFrom = Number(other.f);
      if (Number.isNaN(otherFrom)) {
        return;
      }
      if (otherFrom > currentFrom && (nextLimit === null || otherFrom < nextLimit)) {
        nextLimit = otherFrom;
      }
    });
    return {
      ...tier,
      t: nextLimit === null ? '∞' : String(nextLimit),
    };
  });
};

const getValidationSchema = (rateType: RateType, requiresTimeUnit: boolean) =>
  yup.object({
    display_name: yup.string().required('Required'),
    resource: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null || Number.isNaN(originalValue)
          ? undefined
          : value,
      )
      .typeError('Required')
      .required('Required'),
    pricing: yup.object({
      attribute: yup.string(),
      attribute_unit: yup.mixed().nullable(),
      time_unit: requiresTimeUnit
        ? yup.string().required('Required')
        : yup.string().nullable(),
      prices: yup
        .array()
        .of(
          yup.object({
            f:
              rateType === 'tiered'
                ? yup
                    .string()
                    .test('required', 'Required', (value) => value !== undefined && value !== null && value !== '')
                : yup.string().nullable(),
            p: yup
              .string()
              .test('required', 'Required', (value) => value !== undefined && value !== null && value !== ''),
            t: yup.string().nullable(),
          }),
        )
        .min(1, 'Add at least one price'),
    }),
    start_dt: yup.mixed().nullable(),
    end_dt: yup.mixed().nullable(),
    calculate_metrics_pricing_per_time_unit: yup.boolean(),
    free_resources_for_related_resource: yup.string().nullable(),
    conditions: yup.array(),
    modifiers: yup.array(),
  });

const getAttributeByName = (
  resource: PricingRuleResource | null,
  attributeName: string,
): PricingRuleAttribute | null => {
  if (!resource) {
    return null;
  }
  return (
    resource.attributes?.find((attribute) => attribute.name === attributeName) ?? null
  );
};

const attributeRequiresUnit = (
  resource: PricingRuleResource | null,
  attributeName: string,
) => {
  const attribute = getAttributeByName(resource, attributeName);
  if (!attribute) {
    return false;
  }
  if (attribute.choices) {
    return false;
  }
  return (
    attribute.type === 'integer' &&
    Boolean(attribute.value_size) &&
    UNIT_SIZE_CODES.includes(attribute.value_size ?? '')
  );
};

const pricingAttributeSupportsUnit = (
  resource: PricingRuleResource | null,
  attributeName: string,
) => {
  const attribute = getAttributeByName(resource, attributeName);
  if (!attribute?.value_size) {
    return false;
  }
  return UNIT_SIZE_CODES.includes(attribute.value_size);
};

const getFilterInputType = (
  resource: PricingRuleResource | null,
  attributeName: string,
): 'choices' | 'string' | 'number' | 'datetime' | null => {
  const attribute = getAttributeByName(resource, attributeName);
  if (!attribute) {
    return null;
  }
  if (attribute.choices_dict && Object.keys(attribute.choices_dict).length) {
    return 'choices';
  }
  if (attribute.type === 'string') {
    return 'string';
  }
  if (attribute.type === 'integer') {
    return 'number';
  }
  if (attribute.type === 'datetime') {
    return 'datetime';
  }
  return null;
};

const getOperatorsForAttribute = (
  resource: PricingRuleResource | null,
  attributeName: string,
  options: PricingRuleCreateOptions | null,
) => {
  if (!resource || !options) {
    return [];
  }
  const attribute = getAttributeByName(resource, attributeName);
  if (!attribute) {
    return [];
  }
  const map =
    attribute.choices ||
    attribute.choices_dict ||
    attribute.type === 'string'
      ? options.string_operators
      : attribute.type === 'integer' || attribute.type === 'datetime'
      ? options.number_operators
      : {};
  return Object.entries(map).map(([value, label]) => ({
    value,
    label: String(label),
  }));
};

const hasPricingAttribute = (resource: PricingRuleResource | null) => {
  if (!resource) {
    return false;
  }
  if (resource.type !== 'service') {
    return false;
  }
  return resource.attributes?.some((attribute) => attribute.type === 'integer');
};

const getMetricHelpText = (
  resource: PricingRuleResource | null,
  attributeName: string,
) => {
  if (!resource?.metrics?.length) {
    return null;
  }
  const metric = resource.metrics.find((item) => item.name === attributeName);
  return metric?.help_text ?? null;
};

const metricAllowsPerSecond = (
  resource: PricingRuleResource | null,
  attributeName: string,
) => {
  if (!resource?.metrics?.length) {
    return false;
  }
  return resource.metrics.some((metric) => {
    if (attributeName && metric.name !== attributeName) {
      return false;
    }
    if (metric.billing_based_on_existence) {
      return true;
    }
    if (!metric.reaggregation) {
      return false;
    }
    return ['mean', 'max', 'min'].includes(metric.reaggregation);
  });
};

const normalizeListValue = (raw: string) =>
  raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .join(',');

const buildPayload = (
  values: RuleFormValues,
  resource: PricingRuleResource | null,
  rateType: RateType,
  planId: string | null,
) => {
  const payload: any = {
    display_name: values.display_name,
    resource: Number(values.resource),
    calculate_metrics_pricing_per_time_unit: values.calculate_metrics_pricing_per_time_unit,
    pricing: {
      attribute: values.pricing.attribute,
      attribute_unit: values.pricing.attribute_unit || null,
      prices: [],
    },
    conditions: [],
    modifiers: [],
  };

  if (planId) {
    payload.plan = Number(planId);
  }

  if (values.pricing.time_unit) {
    payload.pricing.time_unit = values.pricing.time_unit;
  }

  const priceSource =
    rateType === 'flat'
      ? [values.pricing.prices[0] ?? createEmptyPriceTier()]
      : values.pricing.prices;

  payload.pricing.prices = priceSource
    .filter(Boolean)
    .map((tier, index) => {
      const fromValue =
        rateType === 'flat'
          ? 0
          : tier.f === '' || tier.f === null || tier.f === undefined
          ? 0
          : Number(tier.f);

      const entry: any = {
        f: fromValue,
        p:
          tier.p === '' || tier.p === null || tier.p === undefined
            ? 0
            : Number(tier.p),
      };

      if (rateType === 'tiered') {
        const limit =
          index === priceSource.length - 1
            ? null
            : tier.t && tier.t !== '∞'
            ? Number(tier.t)
            : null;
        if (limit !== null && !Number.isNaN(limit)) {
          entry.t = limit;
        }
      }

      return entry;
    });

  if (values.start_dt) {
    payload.start_dt = dateValueToIsoString(values.start_dt);
  } else {
    payload.start_dt = null;
  }

  if (values.end_dt) {
    payload.end_dt = dateValueToIsoString(values.end_dt);
  } else {
    payload.end_dt = null;
  }

  if (resource?.type === 'internal') {
    payload.start_dt = null;
    payload.end_dt = null;
  }

  payload.conditions = values.conditions.map((condition) => {
    const attrUnit = attributeRequiresUnit(resource, condition.attribute)
      ? condition.attribute_unit
      : null;
    let value = condition.value || '';
    if (value && isListOperator(condition.operator)) {
      value = normalizeListValue(value);
    }
    return {
      attribute: condition.attribute,
      operator: condition.operator,
      value,
      attribute_unit: attrUnit,
    };
  });

  payload.modifiers = values.modifiers.map((modifier) => {
    const attrUnit = attributeRequiresUnit(resource, modifier.attribute)
      ? modifier.attribute_unit
      : null;
    let value = modifier.value || '';
    if (value && isListOperator(modifier.operator)) {
      value = normalizeListValue(value);
    }
    const entry: any = {
      name: modifier.name,
      attribute: modifier.attribute,
      operator: modifier.operator,
      value,
      attribute_unit: attrUnit,
      price:
        modifier.price === '' || modifier.price === null || modifier.price === undefined
          ? 0
          : Number(modifier.price),
      price_is_percent: Boolean(modifier.price_is_percent),
    };
    if (!modifier.price_is_percent) {
      entry.time_unit = modifier.time_unit || null;
    }
    return entry;
  });

  if (values.free_resources_for_related_resource) {
    payload.free_resources_for_related_resource = Number(
      values.free_resources_for_related_resource,
    );
  }

  if (payload.plan === undefined) {
    delete payload.plan;
  }

  return payload;
};

const CreateEditRule: React.FC = () => {
  const { planId: planIdParam, ruleId } = useParams<{ planId?: string; ruleId?: string }>();
  const navigate = useNavigate();
  const { viewAlert } = useUi();

  const [createOptions, setCreateOptions] = useState<PricingRuleCreateOptions | null>(null);
  const [pricingPlan, setPricingPlan] = useState<any | null>(null);
  const [initialValues, setInitialValues] = useState<RuleFormValues>(defaultFormValues);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rateType, setRateType] = useState<RateType>('flat');
  const [requiresTimeUnit, setRequiresTimeUnit] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(planIdParam ?? null);
  const resourceChangeRef = useRef(false);

  useEffect(() => {
    if (planIdParam) {
      setCurrentPlanId(planIdParam);
    }
  }, [planIdParam]);

  const validationSchema = useMemo(
    () => getValidationSchema(rateType, requiresTimeUnit),
    [rateType, requiresTimeUnit],
  );

  const isEdit = Boolean(ruleId);

  const formik = useFormik<RuleFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const resource =
        createOptions?.resources.find((item) => item.id === Number(values.resource)) ?? null;

      const manualErrors: FormikErrors<RuleFormValues> = {};

      if (values.conditions.length) {
        const condErrors = values.conditions.map((condition) => {
          const entry: Partial<ConditionForm> = {};
          if (!condition.attribute) {
            entry.attribute = 'Required';
          }
          if (!condition.operator) {
            entry.operator = 'Required';
          }
          if (!condition.value) {
            entry.value = 'Required';
          }
          if (attributeRequiresUnit(resource, condition.attribute) && !condition.attribute_unit) {
            entry.attribute_unit = 'Required';
          }
          return entry;
        });
        if (condErrors.some((entry) => Object.keys(entry).length > 0)) {
          manualErrors.conditions = condErrors as any;
        }
      }

      if (values.modifiers.length) {
        const modErrors = values.modifiers.map((modifier) => {
          const entry: Partial<ModifierForm> = {};
          if (!modifier.name) {
            entry.name = 'Required';
          }
          if (!modifier.attribute) {
            entry.attribute = 'Required';
          }
          if (!modifier.operator) {
            entry.operator = 'Required';
          }
          if (!modifier.value) {
            entry.value = 'Required';
          }
          if (modifier.price === '') {
            entry.price = 'Required';
          }
          if (!modifier.price_is_percent && !modifier.time_unit) {
            entry.time_unit = 'Required';
          }
          if (attributeRequiresUnit(resource, modifier.attribute) && !modifier.attribute_unit) {
            entry.attribute_unit = 'Required';
          }
          return entry;
        });
        if (modErrors.some((entry) => Object.keys(entry).length > 0)) {
          manualErrors.modifiers = modErrors as any;
        }
      }

      if (
        values.free_resources_for_related_resource &&
        Number.isNaN(Number(values.free_resources_for_related_resource))
      ) {
        manualErrors.free_resources_for_related_resource = 'Must be a number' as any;
      }

      if (Object.keys(manualErrors).length) {
        formik.setErrors(manualErrors);
        viewAlert({
          severity: 'error',
          message: 'Please fill in all required pricing rule fields.',
        });
        return;
      }

      setSubmitting(true);
      const payload = buildPayload(values, resource, rateType, currentPlanId);
      try {
        if (isEdit && ruleId) {
          await openstackPlanService.updatePriceRule(ruleId, payload);
          viewAlert({ severity: 'info', message: 'Rule updated successfully' });
        } else {
          if (!currentPlanId) {
            viewAlert({ severity: 'error', message: 'Plan identifier is missing.' });
            setSubmitting(false);
            return;
          }
          await openstackPlanService.createPriceRule(payload);
          viewAlert({ severity: 'info', message: 'Rule created successfully' });
        }
        const redirectPlanId = currentPlanId ?? (payload.plan ? String(payload.plan) : null);
        if (redirectPlanId) {
          navigate(`/settings/openstack-plans/plan/${redirectPlanId}`);
        } else {
          navigate(-1);
        }
      } catch (error: any) {
        if (error?.response?.data?.detail) {
          viewAlert({ severity: 'error', message: error.response.data.detail });
        } else {
          viewAlert({ severity: 'error', message: 'Failed to save pricing rule' });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const selectedResource = useMemo(() => {
    if (!createOptions) {
      return null;
    }
    const resourceId = formik.values.resource;
    if (resourceId === '' || resourceId === null || resourceId === undefined) {
      return null;
    }
    return (
      createOptions.resources.find((resource) => resource.id === Number(resourceId)) ?? null
    );
  }, [createOptions, formik.values.resource]);

  useEffect(() => {
    const isAttributeDisplay = Boolean(selectedResource?.attribute_display);
    const isMetricPerTime = Boolean(
      selectedResource &&
        formik.values.calculate_metrics_pricing_per_time_unit &&
        metricAllowsPerSecond(selectedResource, formik.values.pricing.attribute),
    );
    setRequiresTimeUnit(isAttributeDisplay || isMetricPerTime);
  }, [
    selectedResource,
    formik.values.calculate_metrics_pricing_per_time_unit,
    formik.values.pricing.attribute,
  ]);

  useEffect(() => {
    if (
      !selectedResource?.attribute_display &&
      !formik.values.calculate_metrics_pricing_per_time_unit &&
      formik.values.pricing.time_unit
    ) {
      formik.setFieldValue('pricing.time_unit', null);
    }
  }, [formik.values.calculate_metrics_pricing_per_time_unit]);

  useEffect(() => {
    if (!selectedResource) {
      if (formik.values.pricing.attribute_unit) {
        formik.setFieldValue('pricing.attribute_unit', null);
      }
      return;
    }
    if (
      !pricingAttributeSupportsUnit(selectedResource, formik.values.pricing.attribute) &&
      formik.values.pricing.attribute_unit
    ) {
      formik.setFieldValue('pricing.attribute_unit', null);
    }
  }, [selectedResource, formik.values.pricing.attribute]);

  useEffect(() => {
    const loadData = async () => {
      // setLoading(true);
      try {
        const optionsRes = await openstackPlanService.getPriceRuleCreateOptions();
        const normalizedOptions = normalizeOptions(optionsRes.data);
        setCreateOptions(normalizedOptions);

        let planIdToUse = planIdParam ?? currentPlanId ?? null;

        if (ruleId) {
          const ruleRes = await openstackPlanService.getPriceRuleById(ruleId);
          const mapped = mapRuleFromApi(ruleRes.data);
          setInitialValues(mapped);
          setRateType(mapped.pricing.prices.length > 1 ? 'tiered' : 'flat');
          if (ruleRes.data.plan !== undefined && ruleRes.data.plan !== null) {
            planIdToUse = String(ruleRes.data.plan);
            setCurrentPlanId(planIdToUse);
          }
        } else {
          setInitialValues({
            ...defaultFormValues,
            resource: '',
            pricing: {
              ...defaultFormValues.pricing,
              prices: [createEmptyPriceTier()],
            },
          });
          setRateType('flat');
        }

        if (planIdToUse) {
          const planRes = await openstackPlanService.getPlanById(planIdToUse);
          setPricingPlan(planRes.data);
          setCurrentPlanId(String(planRes.data.id));
        } else {
          setPricingPlan(null);
        }
      } catch (error: any) {
        if (error?.response?.data?.detail) {
          viewAlert({ severity: 'error', message: error.response.data.detail });
        } else {
          viewAlert({ severity: 'error', message: 'Failed to load pricing rule data' });
        }
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [planIdParam, ruleId]);

  useEffect(() => {
    if (!selectedResource) {
      return;
    }
    if (!resourceChangeRef.current) {
      return;
    }
    resourceChangeRef.current = false;

    setRateType('flat');
    formik.setFieldValue('pricing.prices', [createEmptyPriceTier()]);
    formik.setFieldValue(
      'pricing.attribute',
      selectedResource.internal_name === 'instance_ip' ? ' ' : '',
    );
    formik.setFieldValue('pricing.attribute_unit', null);
    formik.setFieldValue('pricing.time_unit', null);
    formik.setFieldValue('calculate_metrics_pricing_per_time_unit', false);
    formik.setFieldValue('conditions', []);
    formik.setFieldValue('modifiers', []);
    if (selectedResource.internal_name === 'instance_ip') {
      formik.setFieldValue(
        'free_resources_for_related_resource',
        formik.values.free_resources_for_related_resource || '0',
      );
    } else {
      formik.setFieldValue('free_resources_for_related_resource', '');
    }
  }, [selectedResource]);

  const resourceOptions = useMemo(
    () =>
      createOptions
        ? createOptions.resources.map((resource) => ({
            id: resource.id,
            label: resource.name,
          }))
        : [],
    [createOptions],
  );

  const timeUnitOptions = useMemo(
    () =>
      createOptions
        ? Object.entries(createOptions.time_units).map(([value, label]) => ({
            value,
            label: String(label),
          }))
        : [],
    [createOptions],
  );

  const attributeUnitOptions = useMemo(
    () =>
      createOptions
        ? Object.entries(createOptions.attribute_units).map(([value, label]) => ({
            value,
            label: String(label),
          }))
        : [],
    [createOptions],
  );

  const pricingAttributeOptions = useMemo(() => {
    if (!selectedResource) {
      return [];
    }
    const numericAttributes =
      selectedResource.attributes?.filter(
        (attribute) =>
          attribute.type === 'integer' &&
          !(attribute.name === 'ttl' && selectedResource.name === 'DNS Domains'),
      ) ?? [];
    return [
      { value: ' ', label: 'existence' },
      ...numericAttributes.map((attribute) => ({
        value: attribute.name,
        label: attribute.name,
      })),
    ];
  }, [selectedResource]);

  const metricOptions = useMemo(() => {
    if (!selectedResource?.metrics?.length) {
      return [];
    }
    return selectedResource.metrics.map((metric) => ({
      value: metric.name,
      label: metric.name,
    }));
  }, [selectedResource]);

  const conditionErrors = Array.isArray(formik.errors.conditions)
    ? (formik.errors.conditions as Array<Partial<ConditionForm> | undefined>)
    : [];
  const modifierErrors = Array.isArray(formik.errors.modifiers)
    ? (formik.errors.modifiers as Array<Partial<ModifierForm> | undefined>)
    : [];

  const metricHelp = useMemo(
    () => getMetricHelpText(selectedResource, formik.values.pricing.attribute),
    [selectedResource, formik.values.pricing.attribute],
  );

  const allowMetricPerTime = useMemo(
    () => metricAllowsPerSecond(selectedResource, formik.values.pricing.attribute),
    [selectedResource, formik.values.pricing.attribute],
  );

  const showPricingAttribute =
    selectedResource?.attribute_display && hasPricingAttribute(selectedResource);

  const shouldShowFreeResources =
    selectedResource && (selectedResource.internal_name === 'instance_ip' || selectedResource.internal_name === 'router');

  const attributeSupportsUnit = pricingAttributeSupportsUnit(
    selectedResource,
    formik.values.pricing.attribute,
  );

  const planLabel = pricingPlan ? `${pricingPlan.name} • ${pricingPlan.currency}` : '';

  const handleResourceSelect = (value: any) => {
    if (value === undefined || value === null || value === '') {
      formik.setFieldValue('resource', '');
      return;
    }
    resourceChangeRef.current = true;
    formik.setFieldValue('resource', Number(value));
  };

  const handleRateTypeChange = (next: string) => {
    if (next !== 'flat' && next !== 'tiered') {
      return;
    }
    const nextType = next as RateType;
    setRateType(nextType);
    if (nextType === 'flat') {
      const first = formik.values.pricing.prices[0] ?? createEmptyPriceTier();
      formik.setFieldValue('pricing.prices', [
        {
          ...createEmptyPriceTier(),
          p: first.p,
        },
      ]);
    } else {
      const normalized = recalcTierBounds(
        formik.values.pricing.prices.length
          ? formik.values.pricing.prices
          : [createEmptyPriceTier()],
      );
      formik.setFieldValue('pricing.prices', normalized);
    }
  };

  const handlePriceFieldChange = (index: number, field: keyof PriceTier, value: string) => {
    const updated = formik.values.pricing.prices.map((tier, idx) =>
      idx === index ? { ...tier, [field]: value } : tier,
    );
    const next =
      rateType === 'tiered' && (field === 'f' || field === 't')
        ? recalcTierBounds(updated)
        : updated;
    formik.setFieldValue('pricing.prices', next);
  };

  const handleAddPriceTier = () => {
    const updated = [...formik.values.pricing.prices, { f: '', p: '', t: '∞' }];
    formik.setFieldValue('pricing.prices', recalcTierBounds(updated));
  };

  const handleRemovePriceTier = (index: number) => {
    const updated = formik.values.pricing.prices.filter((_, idx) => idx !== index);
    const sanitized = updated.length ? updated : [createEmptyPriceTier()];
    formik.setFieldValue(
      'pricing.prices',
      rateType === 'tiered' ? recalcTierBounds(sanitized) : sanitized,
    );
  };

  const addCondition = () => {
    formik.setFieldValue('conditions', [...formik.values.conditions, createEmptyCondition()]);
  };

  const removeCondition = (index: number) => {
    const next = formik.values.conditions.filter((_, idx) => idx !== index);
    formik.setFieldValue('conditions', next);
  };

  const updateCondition = (index: number, patch: Partial<ConditionForm>) => {
    const next = formik.values.conditions.map((condition, idx) =>
      idx === index ? { ...condition, ...patch } : condition,
    );
    formik.setFieldValue('conditions', next);
  };

  const addModifier = () => {
    formik.setFieldValue('modifiers', [...formik.values.modifiers, createEmptyModifier()]);
  };

  const removeModifier = (index: number) => {
    const next = formik.values.modifiers.filter((_, idx) => idx !== index);
    formik.setFieldValue('modifiers', next);
  };

  const updateModifier = (index: number, patch: Partial<ModifierForm>) => {
    const next = formik.values.modifiers.map((modifier, idx) =>
      idx === index ? { ...modifier, ...patch } : modifier,
    );
    formik.setFieldValue('modifiers', next);
  };

  const parseSelectedValues = (
    value: string,
    choices: { value: string; label: string }[],
  ): { value: string; label: string }[] => {
    if (!value) return [];
    return value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
      .map((v) => {
        const match = choices.find((c) => c.value === v);
        return match || { value: v, label: v };
      });
  };

  const handleMultiSelectChange = (
    index: number,
    item: { value: string; label: string },
    currentValue: string,
    updater: (index: number, patch: any) => void,
  ) => {
    const current = currentValue
      ? currentValue.split(',').map((v) => v.trim()).filter(Boolean)
      : [];
    const exists = current.includes(item.value);
    const next = exists
      ? current.filter((v) => v !== item.value)
      : [...current, item.value];
    updater(index, { value: next.join(',') });
  };

  if (loading || !createOptions) {
    return <TopProgress loading />;
  }

  return (
    <PageWrapper>
      <HeaderRow>
        <Typography variant="h3">
          {isEdit ? 'Edit Pricing Rule' : 'Create Pricing Rule'}
        </Typography>
        {planLabel ? <Typography variant="body">{planLabel}</Typography> : null}
      </HeaderRow>
      <form onSubmit={formik.handleSubmit}>
        <SectionCard>
          <SectionHeading>
            <Typography variant="h4">General</Typography>
            <SectionDescription>
              Choose where the rule applies and provide a readable name that will be shown in
              the plan details.
            </SectionDescription>
          </SectionHeading>
          <FieldsRow>
            <FieldCol>
              <Select
                required
                title="Resource Type"
                data={resourceOptions}
                selectedValue={formik.values.resource}
                onChange={handleResourceSelect}
                fieldName="resource"
                errors={formik.errors}
                touched={formik.touched}
              />
            </FieldCol>
            <FieldCol>
              <Input
                required
                title="Display Name"
                fieldName="display_name"
                placeholder="Enter rule name"
                values={formik.values}
                handleChange={formik.handleChange}
                errors={formik.errors}
                touched={formik.touched}
              />
            </FieldCol>
          </FieldsRow>
          {selectedResource && selectedResource.type !== 'internal' ? (
            <>
              <SectionDescription>
                Set optional start and end dates if the price should only apply for a limited
                period. Leave both empty to make the rule permanent.
              </SectionDescription>
              <FieldsRow>
                <FieldCol>
                  <DatePickerCustom
                    title="Start Date"
                    value={formik.values.start_dt}
                    onChange={(value) => formik.setFieldValue('start_dt', value)}
                    hideTimeZone
                    showMonthAndYearPickers
                  />
                </FieldCol>
                <FieldCol>
                  <DatePickerCustom
                    title="End Date"
                    value={formik.values.end_dt}
                    onChange={(value) => formik.setFieldValue('end_dt', value)}
                    hideTimeZone
                    showMonthAndYearPickers
                  />
                </FieldCol>
              </FieldsRow>
            </>
          ) : null}
          {shouldShowFreeResources ? (
            <FieldsRow>
              <FieldCol>
                <Input
                  title="Free resources per related instance"
                  placeholder="Number of free IPs"
                  value={formik.values.free_resources_for_related_resource}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    formik.setFieldValue(
                      'free_resources_for_related_resource',
                      event.target.value,
                    )
                  }
                  manualErr={
                    (formik.errors as FormikErrors<RuleFormValues>)
                      ?.free_resources_for_related_resource as string
                  }
                  manualTouched={formik.submitCount > 0}
                  type="number"
                />
              </FieldCol>
            </FieldsRow>
          ) : null}
        </SectionCard>

        <SectionCard>
          <SectionHeading>
            <Typography variant="h4">Pricing</Typography>
            <SectionDescription>
              Define how usage should be measured and priced. Tiered pricing automatically
              computes the “to” boundary based on the next tier.
            </SectionDescription>
          </SectionHeading>
          {showPricingAttribute ? (
            <FieldsRow>
              <FieldCol>
                <Select
                  required
                  title="Attribute"
                  data={pricingAttributeOptions}
                  selectedValue={formik.values.pricing.attribute}
                  onChange={(value) => formik.setFieldValue('pricing.attribute', value)}
                  fieldName="attribute"
                  errors={formik.errors.pricing as any}
                  touched={formik.touched.pricing as any}
                />
              </FieldCol>
              <FieldCol>
                <Select
                  title="Attribute Unit"
                  data={attributeUnitOptions}
                  selectedValue={formik.values.pricing.attribute_unit}
                  onChange={(value) => formik.setFieldValue('pricing.attribute_unit', value)}
                  fieldName="attribute_unit"
                  errors={formik.errors.pricing as any}
                  touched={formik.touched.pricing as any}
                  disabled={!attributeSupportsUnit}
                />
              </FieldCol>
            </FieldsRow>
          ) : null}
          {selectedResource?.attribute_display ? (
            <FieldsRow>
              <FieldCol style={{ maxWidth: 320 }}>
                <Select
                  required
                  title="Time Unit"
                  data={timeUnitOptions}
                  selectedValue={formik.values.pricing.time_unit}
                  onChange={(value) => formik.setFieldValue('pricing.time_unit', value)}
                  fieldName="time_unit"
                  errors={formik.errors.pricing as any}
                  touched={formik.touched.pricing as any}
                />
              </FieldCol>
            </FieldsRow>
          ) : null}
          {selectedResource?.metric_display ? (
            <>
              <FieldsRow>
                <FieldCol>
                  <Select
                    required
                    title="Metric"
                    data={metricOptions}
                    selectedValue={formik.values.pricing.attribute}
                    onChange={(value) => formik.setFieldValue('pricing.attribute', value)}
                    fieldName="attribute"
                    errors={formik.errors.pricing as any}
                    touched={formik.touched.pricing as any}
                  />
                </FieldCol>
              </FieldsRow>
              {metricHelp ? <SectionDescription>{metricHelp}</SectionDescription> : null}
              {allowMetricPerTime ? (
                <CheckBox
                  title="Calculate metrics pricing per time unit"
                  value={formik.values.calculate_metrics_pricing_per_time_unit}
                  onChange={() =>
                    formik.setFieldValue(
                      'calculate_metrics_pricing_per_time_unit',
                      !formik.values.calculate_metrics_pricing_per_time_unit,
                    )
                  }
                />
              ) : null}
              {formik.values.calculate_metrics_pricing_per_time_unit ? (
                <FieldCol style={{ maxWidth: 320 }}>
                  <Select
                    required
                    title="Time Unit"
                    data={timeUnitOptions}
                    selectedValue={formik.values.pricing.time_unit}
                    onChange={(value) => formik.setFieldValue('pricing.time_unit', value)}
                    fieldName="time_unit"
                    errors={formik.errors.pricing as any}
                    touched={formik.touched.pricing as any}
                  />
                </FieldCol>
              ) : null}
            </>
          ) : null}
          {selectedResource?.metric_display ? (
            <FieldCol style={{ maxWidth: 320 }}>
              <RadioButtonGroup
                title="Pricing model"
                buttons={[
                  { title: 'Flat', value: 'flat' },
                  { title: 'Tiered', value: 'tiered' },
                ]}
                selectedButton={rateType}
                onChange={handleRateTypeChange}
              />
            </FieldCol>
          ) : null}
          {formik.values.pricing.prices.map((tier, index) => (
            <DynamicRow key={`price-tier-${index}`}>
              {rateType === 'tiered' ? (
                <>
                  <FieldCol style={{ maxWidth: 200 }}>
                    <Input
                      required
                      title="From"
                      type="number"
                      value={tier.f}
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handlePriceFieldChange(index, 'f', event.target.value)
                      }
                      manualErr={
                        ((formik.errors.pricing as any)?.prices?.[index]?.f as string) ?? ''
                      }
                      manualTouched={formik.submitCount > 0}
                    />
                  </FieldCol>
                  <FieldCol style={{ maxWidth: 200 }}>
                    <Input
                      title="To"
                      value={tier.t}
                      handleChange={() => null}
                      readOnly
                      disabled
                    />
                  </FieldCol>
                </>
              ) : null}
              <FieldCol style={{ maxWidth: 220 }}>
                <Input
                  required
                  title={`Price ${pricingPlan ? `(${pricingPlan.currency})` : ''}`}
                  type="number"
                  value={tier.p}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handlePriceFieldChange(index, 'p', event.target.value)
                  }
                  manualErr={
                    ((formik.errors.pricing as any)?.prices?.[index]?.p as string) ?? ''
                  }
                  manualTouched={formik.submitCount > 0}
                />
              </FieldCol>
              {rateType === 'tiered' ? (
                <RowActions>
                  <Button
                    variant="stroke"
                    size="small"
                    title="Remove"
                    onClick={() => handleRemovePriceTier(index)}
                  />
                </RowActions>
              ) : null}
            </DynamicRow>
          ))}
          {rateType === 'tiered' ? (
            <AddButtonSlot>
              <Button variant="secondary" size="small" title="Add tier" onClick={handleAddPriceTier} />
            </AddButtonSlot>
          ) : null}
        </SectionCard>

        {selectedResource && selectedResource.attributes?.length ? (
          <SectionCard>
            <SectionHeading>
              <Typography variant="h4">Filters</Typography>
              <SectionDescription>
                The rule applies only when all filters evaluate to true. Leave the section empty
                to make the rule global for the selected resource.
              </SectionDescription>
            </SectionHeading>
            {formik.values.conditions.map((condition, index) => {
              const attribute = getAttributeByName(selectedResource, condition.attribute);
              const inputType = getFilterInputType(selectedResource, condition.attribute);
              const operatorOptions = getOperatorsForAttribute(
                selectedResource,
                condition.attribute,
                createOptions,
              );
              const choiceOptions =
                attribute?.choices_dict && Object.keys(attribute.choices_dict).length
                  ? Object.entries(attribute.choices_dict).map(([value, meta]) => ({
                      value,
                      label: meta?.display || value,
                    }))
                  : [];
              const errorEntry = conditionErrors[index] ?? {};
              const showAttributeUnit = attributeRequiresUnit(
                selectedResource,
                condition.attribute,
              );
              return (
                <DynamicRow key={`condition-${index}`}>
                  <FieldCol style={{ maxWidth: 220 }}>
                    <Select
                      required
                      title="Attribute"
                      data={
                        (selectedResource.attributes || []).map((attr) => ({
                          value: attr.name,
                          label: attr.name,
                        })) ?? []
                      }
                      selectedValue={condition.attribute}
                      onChange={(value) =>
                        updateCondition(index, {
                          attribute: value,
                          operator: '',
                          value: '',
                          attribute_unit: null,
                        })
                      }
                      fieldName="attribute"
                      errors={errorEntry}
                      touched={formik.submitCount > 0 ? { attribute: true } : undefined}
                    />
                  </FieldCol>
                  <FieldCol style={{ maxWidth: 220 }}>
                    <Select
                      required
                      title="Operator"
                      data={operatorOptions}
                      selectedValue={condition.operator}
                      onChange={(value) =>
                        updateCondition(index, {
                          operator: value,
                          value: '',
                        })
                      }
                      fieldName="operator"
                      errors={errorEntry}
                      touched={formik.submitCount > 0 ? { operator: true } : undefined}
                    />
                  </FieldCol>
                  {inputType === 'choices' && isListOperator(condition.operator) ? (
                    <FieldCol>
                      <Select
                        required
                        title="Values"
                        multi
                        hasSearch
                        data={choiceOptions}
                        selectedValue={parseSelectedValues(condition.value, choiceOptions)}
                        onChange={() => {}}
                        multipleOnChange={(item: any) =>
                          handleMultiSelectChange(
                            index,
                            item,
                            condition.value,
                            updateCondition,
                          )
                        }
                        fieldName="value"
                        errors={errorEntry}
                        touched={formik.submitCount > 0 ? { value: true } : undefined}
                      />
                    </FieldCol>
                  ) : inputType === 'choices' ? (
                    <FieldCol>
                      <Select
                        required
                        title="Value"
                        data={choiceOptions}
                        selectedValue={condition.value}
                        onChange={(value) => updateCondition(index, { value })}
                        fieldName="value"
                        errors={errorEntry}
                        touched={formik.submitCount > 0 ? { value: true } : undefined}
                      />
                    </FieldCol>
                  ) : (
                    <FieldCol>
                      <Input
                        required
                        title="Value"
                        type={
                          inputType === 'number'
                            ? 'number'
                            : inputType === 'datetime'
                            ? 'datetime-local'
                            : 'text'
                        }
                        value={condition.value}
                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          updateCondition(index, { value: event.target.value })
                        }
                        manualErr={errorEntry.value as string}
                        manualTouched={formik.submitCount > 0}
                      />
                    </FieldCol>
                  )}
                  {showAttributeUnit ? (
                    <FieldCol style={{ maxWidth: 200 }}>
                      <Select
                        required
                        title="Attribute Unit"
                        data={attributeUnitOptions}
                        selectedValue={condition.attribute_unit}
                        onChange={(value) => updateCondition(index, { attribute_unit: value })}
                        fieldName="attribute_unit"
                        errors={errorEntry}
                        touched={formik.submitCount > 0 ? { attribute_unit: true } : undefined}
                      />
                    </FieldCol>
                  ) : null}
                  <RowActions>
                    <Button
                      variant="stroke"
                      size="small"
                      title="Remove"
                      onClick={() => removeCondition(index)}
                    />
                  </RowActions>
                </DynamicRow>
              );
            })}
            <AddButtonSlot>
              <Button variant="secondary" size="small" title="Add filter" onClick={addCondition} />
            </AddButtonSlot>
          </SectionCard>
        ) : null}

        {selectedResource?.attribute_display ? (
          <SectionCard>
            <SectionHeading>
              <Typography variant="h4">Price modifiers</Typography>
              <SectionDescription>
                Modifiers add or subtract an additional amount when their condition is met. Use
                negative values to subtract.
              </SectionDescription>
            </SectionHeading>
            {formik.values.modifiers.map((modifier, index) => {
              const attribute = getAttributeByName(selectedResource, modifier.attribute);
              const inputType = getFilterInputType(selectedResource, modifier.attribute);
              const operatorOptions = getOperatorsForAttribute(
                selectedResource,
                modifier.attribute,
                createOptions,
              );
              const choiceOptions =
                attribute?.choices_dict && Object.keys(attribute.choices_dict).length
                  ? Object.entries(attribute.choices_dict).map(([value, meta]) => ({
                      value,
                      label: meta?.display || value,
                    }))
                  : [];
              const errorEntry = modifierErrors[index] ?? {};
              const showAttributeUnit = attributeRequiresUnit(
                selectedResource,
                modifier.attribute,
              );
              return (
                <DynamicRow key={`modifier-${index}`}>
                  <FieldCol style={{ maxWidth: 200 }}>
                    <Input
                      required
                      title="Name"
                      value={modifier.name}
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateModifier(index, { name: event.target.value })
                      }
                      manualErr={errorEntry.name as string}
                      manualTouched={formik.submitCount > 0}
                    />
                  </FieldCol>
                  <FieldCol style={{ maxWidth: 220 }}>
                    <Select
                      required
                      title="Attribute"
                      data={
                        (selectedResource.attributes || []).map((attr) => ({
                          value: attr.name,
                          label: attr.name,
                        })) ?? []
                      }
                      selectedValue={modifier.attribute}
                      onChange={(value) =>
                        updateModifier(index, {
                          attribute: value,
                          operator: '',
                          value: '',
                          attribute_unit: null,
                        })
                      }
                      fieldName="attribute"
                      errors={errorEntry}
                      touched={formik.submitCount > 0 ? { attribute: true } : undefined}
                    />
                  </FieldCol>
                  <FieldCol style={{ maxWidth: 200 }}>
                    <Select
                      required
                      title="Operator"
                      data={operatorOptions}
                      selectedValue={modifier.operator}
                      onChange={(value) =>
                        updateModifier(index, {
                          operator: value,
                          value: '',
                        })
                      }
                      fieldName="operator"
                      errors={errorEntry}
                      touched={formik.submitCount > 0 ? { operator: true } : undefined}
                    />
                  </FieldCol>
                  {inputType === 'choices' && isListOperator(modifier.operator) ? (
                    <FieldCol>
                      <Select
                        required
                        title="Values"
                        multi
                        hasSearch
                        data={choiceOptions}
                        selectedValue={parseSelectedValues(modifier.value, choiceOptions)}
                        onChange={() => {}}
                        multipleOnChange={(item: any) =>
                          handleMultiSelectChange(
                            index,
                            item,
                            modifier.value,
                            updateModifier,
                          )
                        }
                        fieldName="value"
                        errors={errorEntry}
                        touched={formik.submitCount > 0 ? { value: true } : undefined}
                      />
                    </FieldCol>
                  ) : inputType === 'choices' ? (
                    <FieldCol>
                      <Select
                        required
                        title="Value"
                        data={choiceOptions}
                        selectedValue={modifier.value}
                        onChange={(value) => updateModifier(index, { value })}
                        fieldName="value"
                        errors={errorEntry}
                        touched={formik.submitCount > 0 ? { value: true } : undefined}
                      />
                    </FieldCol>
                  ) : (
                    <FieldCol>
                      <Input
                        required
                        title="Value"
                        type={
                          inputType === 'number'
                            ? 'number'
                            : inputType === 'datetime'
                            ? 'datetime-local'
                            : 'text'
                        }
                        value={modifier.value}
                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          updateModifier(index, { value: event.target.value })
                        }
                        manualErr={errorEntry.value as string}
                        manualTouched={formik.submitCount > 0}
                      />
                    </FieldCol>
                  )}
                  <FieldCol style={{ maxWidth: 160 }}>
                    <Input
                      required
                      title="Price impact"
                      type="number"
                      value={modifier.price}
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateModifier(index, { price: event.target.value })
                      }
                      manualErr={errorEntry.price as string}
                      manualTouched={formik.submitCount > 0}
                    />
                  </FieldCol>
                  <FieldCol style={{ maxWidth: 200 }}>
                    <CheckBox
                      title="Apply as percent"
                      value={modifier.price_is_percent}
                      onChange={() =>
                        updateModifier(index, {
                          price_is_percent: !modifier.price_is_percent,
                          time_unit: modifier.price_is_percent ? modifier.time_unit : null,
                        })
                      }
                    />
                  </FieldCol>
                  {!modifier.price_is_percent ? (
                    <FieldCol style={{ maxWidth: 200 }}>
                      <Select
                        required
                        title="Time Unit"
                        data={timeUnitOptions}
                        selectedValue={modifier.time_unit}
                        onChange={(value) => updateModifier(index, { time_unit: value })}
                        fieldName="time_unit"
                        errors={errorEntry}
                        touched={formik.submitCount > 0 ? { time_unit: true } : undefined}
                      />
                    </FieldCol>
                  ) : null}
                  {showAttributeUnit ? (
                    <FieldCol style={{ maxWidth: 200 }}>
                      <Select
                        required
                        title="Attribute Unit"
                        data={attributeUnitOptions}
                        selectedValue={modifier.attribute_unit}
                        onChange={(value) => updateModifier(index, { attribute_unit: value })}
                        fieldName="attribute_unit"
                        errors={errorEntry}
                        touched={formik.submitCount > 0 ? { attribute_unit: true } : undefined}
                      />
                    </FieldCol>
                  ) : null}
                  <RowActions>
                    <Button
                      variant="stroke"
                      size="small"
                      title="Remove"
                      onClick={() => removeModifier(index)}
                    />
                  </RowActions>
                </DynamicRow>
              );
            })}
            <AddButtonSlot>
              <Button variant="secondary" size="small" title="Add modifier" onClick={addModifier} />
            </AddButtonSlot>
          </SectionCard>
        ) : null}

        <FooterActions>
          <Button
            variant="stroke"
            size="display"
            title="Cancel"
            onClick={() => navigate(-1)}
          />
          <Button
            variant="primary"
            size="display"
            title={isEdit ? 'Save' : 'Create'}
            type="submit"
            loading={submitting}
          />
        </FooterActions>
      </form>
    </PageWrapper>
  );
};

export default CreateEditRule;
